import React, { useMemo, useRef } from 'react';
import {
  Brief,
  GroupedElements,
  Gutter,
  Grid,
  GridColumn,
  Layout,
  ReadonlyTextarea,
  Well,
} from '@axiom/ui';
import type { TextareaFence } from '@axiom/ui';

import type { MatchingCandidate } from '../../api/chat-api';
import type { ChatMessageEntry } from '../../types/chat.types';
import { ChatMessageOriginators } from '../../enums/chat-message-originators';
import { ChatAnswerUtil } from '../../utils/chat-answer-util';
import { ChatEmailUtil } from '../../utils/chat-email-util';
import { ChatEmail } from '../ChatEmail/ChatEmail';
import { ChatCopyButton } from '../ChatCopyButton/ChatCopyButton';
import { ChatAttachmentList } from '../ChatAttachmentList/ChatAttachmentList';

import { ChatCandidatesButton } from './ChatCandidatesButton';

const PROMPT_MAX_HEIGHT = '264px';

export type ChatTextareaProps = {
  entry: ChatMessageEntry;
  idx: number;
  /*
    Stable by contract: `ChatTurn` is memoized, and a fresh identity here would make its
    `memo` bail out on every keystroke.
  */
  showCandidates: (candidates: MatchingCandidate[]) => void;
};

export const ChatTextarea = ({
  entry,
  idx,
  showCandidates,
}: ChatTextareaProps) => {
  const isAnswer = entry.messageOriginator === ChatMessageOriginators.LLM;

  /*
    On an inner element rather than the root, which is the whole reason there is an inner
    element: the copy button is a sibling of what it copies, and inside the node the ref
    points at its own svg and label would land in the copied markup.
  */
  const contentRef = useRef<HTMLDivElement>(null);

  const segments = entry.text
    ? ChatAnswerUtil.parseAnswer({ text: entry.text })
    : [];

  /* Only the email fence: the flowchart one is the design system's own. */
  const fences = useMemo<Record<string, TextareaFence>>(
    () => ({
      email: {
        canDraw: code => ChatAnswerUtil.isDrawn({ code, language: 'email' }),
        render: ({ code, index }) => (
          <Gutter vertical="16px">
            <ChatEmail
              email={ChatEmailUtil.parseEmail({ text: code })}
              suffix={`${idx}_${index}`}
            />
          </Gutter>
        ),
      },
    }),
    [idx]
  );

  const copyButton = entry.text ? (
    <ChatCopyButton
      contentRef={contentRef}
      markdown={entry.text}
      name="COPY_ANSWER"
      segments={segments}
    />
  ) : null;

  const candidatesButton = entry.matchingCandidates ? (
    <ChatCandidatesButton
      idx={idx}
      matchingCandidates={entry.matchingCandidates}
      showCandidates={showCandidates}
    />
  ) : null;

  const body = (
    <>
      {entry.text ? (
        <div ref={contentRef}>
          <ReadonlyTextarea
            fences={fences}
            formatting="modern"
            name={`DISPLAY_CHAT_${entry.messageOriginator}_${idx}`}
            value={entry.text}
          />
        </div>
      ) : null}
      {isAnswer && (copyButton || candidatesButton) ? (
        <Gutter top="8px">
          <Layout name={`CHAT_ANSWER_ACTIONS_${idx}`} position="middle">
            {/* A lone button is a button: the group is for a run of them. */}
            {copyButton && candidatesButton ? (
              <GroupedElements
                disableHover
                divider
                name={`CHAT_ANSWER_ACTIONS_GROUP_${idx}`}
                outlined
              >
                {copyButton}
                {candidatesButton}
              </GroupedElements>
            ) : (
              (copyButton ?? candidatesButton)
            )}
          </Layout>
        </Gutter>
      ) : null}
    </>
  );

  return (
    <>
      {entry.attachments?.length ? (
        // Above the words and outside the bubble, held to the prompt's side.
        <Grid>
          <GridColumn floated="right" width={7}>
            <ChatAttachmentList
              attachments={entry.attachments}
              chatId={entry.chatId}
              images="thumbnail"
              name={`CHAT_ATTACHMENTS_${idx}`}
            />
          </GridColumn>
        </Grid>
      ) : null}
      {isAnswer ? (
        <div data-test={`CHAT_MESSAGE_${idx}`}>{body}</div>
      ) : (
        <Grid>
          <GridColumn width={9} floated="right">
            <Layout position="right">
              <Well background="light blue" name={`CHAT_MESSAGE_${idx}`}>
                <Gutter horizontal="8px" vertical="8px">
                  <Brief
                    position="left"
                    pattern="secondary"
                    briefMaxHeight={PROMPT_MAX_HEIGHT}
                    name={`CHAT_PROMPT_BRIEF_${idx}`}
                    variation="minimal"
                  >
                    {body}
                  </Brief>
                </Gutter>
              </Well>
            </Layout>
          </GridColumn>
        </Grid>
      )}
    </>
  );
};
